import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Shield, Users, MessageSquare, Bell } from 'lucide-react';

interface MinorCommunicationRulesAlertProps {
  className?: string;
  compact?: boolean;
}

const MinorCommunicationRulesAlert: React.FC<MinorCommunicationRulesAlertProps> = ({ 
  className = '',
  compact = false 
}) => {
  const rules = [
    {
      icon: Users,
      title: 'Parent Account Required',
      description: 'Your parent must have a "Players Lockeroom Parent" account type.'
    },
    {
      icon: Shield,
      title: 'Connection Needed',
      description: 'They must be connected to you. If you have not provided them with your Connect ID, please provide it so the Parent-to-Minor connection can be made.'
    },
    {
      icon: MessageSquare,
      title: 'Parent Communication First',
      description: 'Communication from agents/NIL representatives will go to your Parent first. The Parent can add you to the messaging if they wish, but for safety on the Players Lockeroom Platform, we will have the Adult acknowledged.'
    },
    {
      icon: Bell,
      title: 'Interest Notifications',
      description: 'You will be notified when someone shows interest. Notification will go to the parent, and you will receive a "Someone looks interested, reach out to your Parent(s) for more information" notification.'
    }
  ];

  if (compact) {
    return (
      <Alert className={`border-blue-500 bg-blue-900/50 ${className}`}>
        <Shield className="h-4 w-4 text-blue-400" />
        <AlertTitle className="text-blue-200 text-sm font-semibold">
          BETA Interest Communication Rules
        </AlertTitle>
        <AlertDescription className="text-blue-300 text-xs mt-1">
          To receive communication from agents, your parent must have a Players Lockeroom Parent account 
          and be connected to you. All agent communication goes through your parent first.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <Alert className="border-amber-500 bg-amber-900/30">
        <Shield className="h-5 w-5 text-amber-400" />
        <AlertTitle className="text-amber-200 font-semibold">
          Important: BETA Interest Communication Rules
        </AlertTitle>
        <AlertDescription className="text-amber-100 mt-2">
          <p className="mb-3">
            In order to receive communication from anyone in BETA status (agents, NIL representatives), 
            all of the following must apply:
          </p>
          <div className="space-y-3">
            {rules.map((rule, index) => (
              <div key={index} className="flex items-start gap-3 bg-amber-950/30 p-3 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <span className="text-amber-400 text-xs font-bold">{index + 1}</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <rule.icon className="h-4 w-4 text-amber-400" />
                    <span className="font-medium text-amber-200">{rule.title}</span>
                  </div>
                  <p className="text-amber-100/80 text-sm mt-1">{rule.description}</p>
                </div>
              </div>
            ))}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default MinorCommunicationRulesAlert;
